import React, { useEffect, useState } from "react";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { logError } from "../lib/logger";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * Botão que solicita permissão e inscreve o navegador para Web Push.
 *
 * Suporta Chrome, Edge, Firefox e Safari 16.4+ (iOS 16.4+ exige PWA instalada).
 * Estados: not-supported | denied | unsupported-context | unsubscribed |
 *          subscribing | subscribed | error.
 */
export function PushNotificationsToggle() {
  const [state, setState] = useState("loading");

  async function checkSubscription() {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      setState("not-supported");
      return;
    }
    if (Notification.permission === "denied") {
      setState("denied");
      return;
    }
    try {
      const reg = await navigator.serviceWorker.getRegistration("/sw.js");
      if (!reg) {
        setState("unsubscribed");
        return;
      }
      const sub = await reg.pushManager.getSubscription();
      setState(sub ? "subscribed" : "unsubscribed");
    } catch (e) {
      setState("unsubscribed");
    }
  }

  useEffect(() => {
    checkSubscription();
  }, []);

  async function subscribe() {
    setState("subscribing");
    try {
      const reg =
        (await navigator.serviceWorker.getRegistration("/sw.js")) ||
        (await navigator.serviceWorker.register("/sw.js"));
      if (Notification.permission !== "granted") {
        const perm = await Notification.requestPermission();
        if (perm !== "granted") {
          toast.error("Permissão negada nas configurações do navegador");
          setState(perm === "denied" ? "denied" : "unsubscribed");
          return;
        }
      }
      const { data: keyData } = await api.get("/push/vapid-public-key");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(keyData.public_key),
      });
      await api.post("/push/subscribe", sub.toJSON());
      setState("subscribed");
      toast.success("Notificações ativadas neste dispositivo");
    } catch (e) {
      logError("push subscribe failed", e);
      toast.error("Falha ao ativar notificações");
      setState("unsubscribed");
    }
  }

  async function unsubscribe() {
    try {
      const reg = await navigator.serviceWorker.getRegistration("/sw.js");
      const sub = await reg?.pushManager.getSubscription();
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setState("unsubscribed");
      toast.success("Notificações desativadas neste dispositivo");
    } catch (e) {
      toast.error("Falha ao desativar");
    }
  }

  if (state === "loading")
    return <span className="text-xs text-slate-400">…</span>;
  if (state === "not-supported")
    return (
      <div
        className="text-xs text-slate-500 inline-flex items-center gap-1"
        data-testid="push-not-supported"
      >
        <BellOff size={12} /> Push não suportado nesse navegador
      </div>
    );
  if (state === "denied")
    return (
      <div
        className="text-xs text-amber-700 inline-flex items-center gap-1"
        data-testid="push-denied"
      >
        <BellOff size={12} /> Permissão bloqueada — habilite nas configurações
      </div>
    );

  return state === "subscribed" ? (
    <button
      type="button"
      onClick={unsubscribe}
      data-testid="push-disable-btn"
      className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
    >
      <Bell size={12} /> Notificações ativas — clique para desativar
    </button>
  ) : (
    <button
      type="button"
      onClick={subscribe}
      data-testid="push-enable-btn"
      disabled={state === "subscribing"}
      className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full border border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100 disabled:opacity-60"
    >
      {state === "subscribing" ? (
        <Loader2 size={12} className="animate-spin" />
      ) : (
        <Bell size={12} />
      )}
      Ativar notificações push
    </button>
  );
}
